import axios from 'axios';
import { ApiUrl } from './ApiUtils';

export interface NsbFlavorsPrecomputes {
  // F2 – Average Recent Price
  avg_recent_price: number | null;
  avg_recent_price_cov: number | null;
  avg_recent_price_transaction_count: number | null;
  avg_recent_quantity: number | null;
  avg_recent_quantity_cov: number | null;
  company_id: number;
  conf_score_avg_recent_price: number;
  conf_score_fred: number;
  conf_score_hist: number;
  conf_score_last_price: number;
  conf_score_qty_adjusted: number;
  conf_score_trend: number;
  cov_contribution_purchasing_org: number | null;
  cov_contribution_quantity: number | null;
  cov_contribution_quarter: number | null;
  cov_contribution_year: number | null;
  // F8 – FRED-Adjusted Price
  fred_adjusted_price: number | null;
  fred_index_growth_rate: number | null;
  fred_r2: number | null;
  // F3-F5 – Historical prices (shared conf score)
  hist_avg_price: number | null;
  hist_avg_quantity: number | null;
  hist_cov: number | null;
  hist_min_price: number | null;
  hist_p25_price: number | null;
  hist_p75_price: number | null;
  hist_quantity_cov: number | null;
  hist_transaction_count: number | null;
  item_id: string | null;
  item_id_cleaned: string | null;
  item_name: string | null;
  item_name_cleaned: string | null;
  // F1 – Last Price
  last_price: number | null;
  last_price_data_source: string | null;
  last_price_date: string | null;
  last_price_quantity: number | null;
  last_price_recency_days: number | null;
  last_price_transaction_id: string | null;
  lookup_type: string;
  net_conf_score_avg_recent_price: number | null;
  net_conf_score_fred: number | null;
  net_conf_score_hist: number | null;
  // Net Confidence Scores (= data quality score × flavor weight)
  net_conf_score_last_price: number | null;
  net_conf_score_qty_adjusted: number | null;
  net_conf_score_trend: number | null;
  // Data source breakdown (% of transactions from PO vs event data)
  pct_event_data: number | null;
  pct_po_data: number | null;
  ppi_commodity_id: string | null;
  ppi_commodity_name: string | null;
  ppi_industry_id: string | null;
  ppi_industry_name: string | null;
  price_qty_correlation: number | null;
  projected_quantity: number | null;
  // F7 – Quantity-Adjusted Price (volume-adjusted projection)
  qty_adjusted_price: number | null;
  qty_price_intercept: number | null;
  qty_price_slope: number | null;
  qty_r2: number | null;
  qty_regr_intercept: number | null;
  qty_slope_normalized: number | null;
  qty_slope_raw: number | null;
  recommended_conf_score: number | null;
  // Recommendation
  recommended_flavor: string | null;
  recommended_price: number | null;
  // F6/F7/F8 – Trend / Qty-Adjusted / FRED (shared trend window quantity)
  trend_avg_quantity: number | null;
  // F6 – Trend Projected Price
  trend_projected_price: number | null;
  trend_r2: number | null;
  trend_regr_intercept: number | null;
  trend_slope_normalized: number | null;
  trend_slope_raw: number | null;
  trend_transaction_count: number | null;
  unit_of_measure_final: string;
}

export interface NsbFlavorsChartPoint {
  date: string;
  price?: number;
  quantity?: number;
}

export interface NsbFlavorsChartData {
  po_price_series: NsbFlavorsChartPoint[] | string | null;
  po_quantity_series: NsbFlavorsChartPoint[] | string | null;
  // Item-name-level max purchase date (the gen pipeline's window anchor). The series is the full
  // 720-day superset; the 360-day flavors slice it to their trailing window relative to this date.
  window_anchor_date?: string | null;
}

export interface NsbFlavorsResponse {
  chart_data: NsbFlavorsChartData | null;
  precomputes: NsbFlavorsPrecomputes | null;
}

// Retry a flaky call a few times before giving up, so a transient hiccup (slow backend / timeout)
// doesn't get silently turned into "no data" and drop the row's price.
const withRetry = async <T>(fn: () => Promise<T>, attempts = 3): Promise<T> => {
  let lastError: unknown;
  for (let i = 0; i < attempts; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (error) {
      lastError = error;
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => { setTimeout(resolve, 200 * (i + 1)); });
    }
  }
  throw lastError;
};

// ─── Batched by-name flavors (performance) ──────────────────────────────────────────────────────
// The benchmark price column fetches flavors once per resolved row — a per-row flood at ~200 items.
// Coalesce a burst of those into ONE POST /smart-baseline/flavors/batch (the backend resolves all in
// two queries). Each caller still receives the same NsbFlavorsResponse it would from the single GET,
// so the renderer / by-name cache / modal are unchanged.
interface PendingFlavorJob {
  itemNameToUse: string;
  resolve: (res: NsbFlavorsResponse) => void;
}

const FLAVORS_BATCH_WINDOW_MS = 50;
const FLAVORS_MAX_BATCH_SIZE = 2000;
const EMPTY_FLAVORS: NsbFlavorsResponse = { chart_data: null, precomputes: null };

let pendingFlavorJobs: PendingFlavorJob[] = [];
let flavorsFlushTimer: ReturnType<typeof setTimeout> | null = null;

const flushFlavorJobs = (): void => {
  const batch = pendingFlavorJobs;
  pendingFlavorJobs = [];
  flavorsFlushTimer = null;
  for (let start = 0; start < batch.length; start += FLAVORS_MAX_BATCH_SIZE) {
    const chunk = batch.slice(start, start + FLAVORS_MAX_BATCH_SIZE);
    const jobs = chunk.map((job, i) => ({ item_name_to_use: job.itemNameToUse, ref: String(i) }));
    // slim: prices only. The full payload carries two 720-day series per item that only a popup
    // reads, so a 1,000-item upload used to fetch ~995 of them for nothing.
    withRetry(() =>
      axios.post(ApiUrl('/arkin/smart-baseline/flavors/batch'), { jobs, slim: true }, { withCredentials: true }))
      .then(({ data }) => {
        const byRef = new Map<string, NsbFlavorsResponse>();
        ((data?.results ?? []) as Array<{
          chart_data: NsbFlavorsResponse['chart_data'];
          precomputes: NsbFlavorsResponse['precomputes'];
          ref: string;
        }>).forEach(r => {
          byRef.set(r.ref, { chart_data: r.chart_data ?? null, precomputes: r.precomputes ?? null });
        });
        chunk.forEach((job, i) => job.resolve(byRef.get(String(i)) ?? EMPTY_FLAVORS));
      })
      // A transient failure recovers instead of leaving the row's price blank: resolve empty so the
      // renderer releases its in-flight marker and re-enrolls on a later render (never rejects).
      .catch(() => chunk.forEach(job => job.resolve(EMPTY_FLAVORS)));
  }
};

// v2: direct join on the matched item_name_to_use that Part 1 produced. Batched (above) so a page of
// items resolves in one request instead of one /flavors call per row.
//
// SLIM tier — prices only (`chart_data` is always null); the load-time call for every matched row.
export const fetchSmartBaselineFlavorsByItemName = (itemNameToUse: string): Promise<NsbFlavorsResponse> =>
  new Promise<NsbFlavorsResponse>(resolve => {
    pendingFlavorJobs.push({ itemNameToUse, resolve });
    if (flavorsFlushTimer === null) flavorsFlushTimer = setTimeout(flushFlavorJobs, FLAVORS_BATCH_WINDOW_MS);
  });

// FULL tier — one item, on popup open. Not batched or coalesced: one request per click, so the slim
// path's 50 ms window would only add latency. Retried, since a failure reads as "no data".
export const fetchSmartBaselineFlavorsFull = (
  itemNameToUse: string,
  catName?: string | null,
): Promise<NsbFlavorsResponse> =>
  withRetry(() =>
    axios.get(ApiUrl('/arkin/smart-baseline/flavors'), {
      params: {
        ...(catName ? { cat_name: catName } : {}),
        item_name_to_use: itemNameToUse,
        lookup_type: 'match',
      },
      withCredentials: true,
    }))
    .then(({ data }) => ({
      chart_data: data?.chart_data ?? null,
      precomputes: data?.precomputes ?? null,
    }) as NsbFlavorsResponse)
    .catch(() => EMPTY_FLAVORS);
